import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faCircleExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { formatByteString } from './FileSizeDisplay';
import {
  listProviderCataloguedUsage,
  type ProviderCataloguedUsageDto,
} from '../api/ame-platform.api';
import {
  getProviderUsage,
  refreshProviderUsage,
  type ProviderUsageReportDto,
} from '../api/provider-storage-usage.api';

/**
 * Per-provider storage, with PHYSICAL and CATALOGUED shown side by side.
 *
 * ── WHY BOTH COLUMNS EXIST ─────────────────────────────────────────────────
 * They answer different questions and they legitimately disagree:
 *
 *   • **Catalogued** sums `storage_assets` rows owned by the provider. It follows
 *     OWNERSHIP. A provider migration flips ownership and deletes nothing, so
 *     the source provider's catalogued figure collapses the moment the flip
 *     lands — while every byte is still on disk.
 *   • **Physical** is what the provider itself reports when listed. It is the
 *     truth about consumption, and it does not move when ownership does.
 *
 * Presenting either one alone would mislead. Presenting catalogued as physical
 * would claim a migration freed space it did not free.
 *
 * ── NO PROVIDER IS HARDCODED ───────────────────────────────────────────────
 * The rows come from the backend provider registry. A provider added later
 * appears here with no change to this file; there is no provider name, driver id
 * or ordering baked into the component.
 *
 * ── COST DISCIPLINE ────────────────────────────────────────────────────────
 * Physical measurement is a full provider listing. This panel reads only the
 * CACHED value on mount (the server lists nothing if it has measured before),
 * and a real scan happens only when an authorised operator presses Scan. Only
 * ENABLED providers are read at all — a disabled provider is not worth a round
 * trip. Freshness is always on screen, so a cached figure is never mistaken for
 * a live one.
 */

function healthTone(status: string): string {
  switch (status) {
    case 'HEALTHY':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'DEGRADED':
      return 'text-amber-600';
    case 'OFFLINE':
      return 'text-destructive';
    default:
      return 'text-muted-foreground';
  }
}

function freshness(u: ProviderUsageReportDto | undefined, scanning: boolean): string {
  if (scanning) return 'Scanning…';
  if (u === undefined) return '—';
  if (u.physical === null) return u.error !== null ? 'Unavailable' : 'Not scanned';
  const age = u.ageSeconds;
  const ago =
    age < 60
      ? `${age}s ago`
      : age < 3600
        ? `${Math.round(age / 60)}m ago`
        : `${Math.round(age / 3600)}h ago`;
  return `${u.cached ? 'Cached' : 'Scanned'} · ${ago}${u.stale ? ' · stale' : ''}`;
}

export interface ProviderBreakdownPanelProps {
  /** Gates the scan action only. Reading cached usage needs view permission. */
  canManage: boolean;
}

export function ProviderBreakdownPanel({ canManage }: ProviderBreakdownPanelProps) {
  const [providers, setProviders] = useState<ProviderCataloguedUsageDto[]>([]);
  const [usage, setUsage] = useState<Record<string, ProviderUsageReportDto>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState<Record<string, boolean>>({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      try {
        const rows = await listProviderCataloguedUsage();
        if (!mountedRef.current) return;
        setProviders(rows);
        setError(null);

        // Cached reads only. A provider that has never been measured returns
        // `physical: null` and is shown as "Not scanned" rather than triggering
        // an unbounded listing behind a dashboard render.
        const enabled = rows.filter((r) => r.isEnabled);
        const results = await Promise.allSettled(
          enabled.map(async (r) => [r.providerId, await getProviderUsage(r.providerId)] as const),
        );
        if (!mountedRef.current) return;
        const next: Record<string, ProviderUsageReportDto> = {};
        for (const res of results) {
          if (res.status === 'fulfilled') next[res.value[0]] = res.value[1];
        }
        setUsage(next);
      } catch (err) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Could not load provider storage.');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();
  }, []);

  /** The ONLY path that performs a real provider listing. Explicit, never automatic. */
  const scan = useCallback(
    async (providerId: string) => {
      if (scanning[providerId]) return; // no duplicate scans of the same provider
      setScanning((s) => ({ ...s, [providerId]: true }));
      try {
        const r = await refreshProviderUsage(providerId);
        if (mountedRef.current) setUsage((u) => ({ ...u, [providerId]: r }));
      } catch (err) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'The scan failed.');
        }
      } finally {
        if (mountedRef.current) setScanning((s) => ({ ...s, [providerId]: false }));
      }
    },
    [scanning],
  );

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="provider-breakdown">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-foreground">Storage by provider</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          <strong className="text-foreground">Physical</strong> is what the provider actually holds.{' '}
          <strong className="text-foreground">Catalogued</strong> is what the asset catalogue owns
          there. A migration moves ownership without deleting the source, so the two differ by
          design afterwards.
        </p>
      </div>

      {error !== null && (
        <p
          className="mb-3 flex items-start gap-2 text-sm text-destructive"
          data-testid="breakdown-error"
        >
          <FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </p>
      )}

      {loading ? (
        <div className="space-y-2" data-testid="breakdown-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : providers.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="breakdown-empty">
          No storage providers are configured.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-2 font-medium">Provider</th>
                <th className="px-2 py-2 text-right font-medium">Objects</th>
                <th className="px-2 py-2 text-right font-medium">Physical</th>
                <th className="px-2 py-2 text-right font-medium">Catalogued</th>
                <th className="px-2 py-2 font-medium">Health</th>
                <th className="px-2 py-2 font-medium">Usage freshness</th>
                {canManage && <th className="px-2 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {providers.map((p) => {
                const u = usage[p.providerId];
                const busy = scanning[p.providerId] === true;
                return (
                  <tr
                    key={p.providerId}
                    className="hover:bg-muted/40"
                    data-testid={`breakdown-row-${p.driverId}`}
                  >
                    <td className="px-2 py-2.5">
                      <span className="font-medium text-foreground">{p.name}</span>
                      <span className="ml-2 text-xs uppercase text-muted-foreground">
                        {p.driverId}
                      </span>
                      {!p.isEnabled && (
                        <span className="ml-2 rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          disabled
                        </span>
                      )}
                      {p.isActive && (
                        <span className="ml-2 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-foreground">
                      {u?.physical?.totalObjects?.toLocaleString() ?? '—'}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums font-medium text-foreground">
                      {formatByteString(u?.physical?.totalBytes)}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-muted-foreground">
                      {formatByteString(p.cataloguedBytes)}
                      <span className="ml-1 text-xs">({p.activeAssets.toLocaleString()})</span>
                    </td>
                    <td
                      className={cn(
                        'px-2 py-2.5 text-xs font-medium',
                        healthTone(p.connectionStatus),
                      )}
                    >
                      {p.connectionStatus}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-muted-foreground">
                      {freshness(u, busy)}
                    </td>
                    {canManage && (
                      <td className="px-2 py-2.5 text-right">
                        <button
                          type="button"
                          data-testid={`breakdown-scan-${p.driverId}`}
                          disabled={busy || !p.isEnabled}
                          onClick={() => void scan(p.providerId)}
                          title={
                            p.isEnabled
                              ? 'Measure this provider now (full listing)'
                              : 'Provider is disabled'
                          }
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <FontAwesomeIcon
                            icon={busy ? faSpinner : faArrowsRotate}
                            spin={busy}
                            className="h-3 w-3"
                          />
                          {busy ? 'Scanning…' : 'Scan'}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/*
            Stated, not hidden: a scan cannot see what object listing does not
            return. Claiming an exact figure would be untrue.
          */}
          <p className="mt-3 text-xs text-muted-foreground" data-testid="breakdown-caveat">
            Physical figures come from provider object listing. Incomplete multipart uploads are not
            returned by listing and are therefore not counted, though a provider may still bill for
            them; on a versioned bucket only current versions are counted.
          </p>
        </div>
      )}
    </div>
  );
}
